import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { EnvelopeSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [promoResult, setPromoResult] = useState(null); // { ok, plan, error }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { email, password, name };
      if (promoCode.trim()) payload.promo_code = promoCode.trim().toUpperCase();
      const res = await axios.post(`${API}/auth/register`, payload);
      if (res.data.promo_applied) {
        setPromoResult({ ok: true, plan: res.data.promo_applied.plan });
      } else if (res.data.promo_error) {
        setPromoResult({ ok: false, error: res.data.promo_error });
      }
      setEmailSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      } else {
        setError('Errore durante la registrazione');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1602577121285-aca49920efac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwd2hpdGV8ZW58MHx8fHwxNzc1MzExNjI4fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="w-full h-full bg-zinc-900/40" />
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Logo size="2xl" />
          </div>

          {emailSent ? (
            /* Step 2: Email sent confirmation */
            <div className="text-center" data-testid="email-sent-confirmation">
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <EnvelopeSimple size={32} weight="bold" className="text-teal-700" />
              </div>
              <h2 className="text-xl font-black text-zinc-900 mb-3">Controlla la tua email!</h2>
              <p className="text-sm text-zinc-500 leading-relaxed mb-2">
                Abbiamo inviato un link di attivazione a:
              </p>
              <p className="text-base font-bold text-zinc-900 mb-6">{email}</p>
              {promoResult?.ok && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 mb-4 text-left" data-testid="promo-applied-banner">
                  <p className="text-sm font-bold text-emerald-800">
                    ✓ Codice promo applicato!
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">
                    {promoResult.plan === 'lifetime' && 'Il tuo account è attivo A VITA — nessun addebito, mai.'}
                    {promoResult.plan === 'yearly' && 'Hai 1 anno di accesso completo gratuito.'}
                    {promoResult.plan === 'quarterly' && 'Hai 3 mesi di accesso completo gratuito.'}
                    {promoResult.plan === 'monthly' && 'Hai 1 mese di accesso completo gratuito.'}
                  </p>
                </div>
              )}
              {promoResult?.ok === false && promoResult.error && (
                <div className="bg-amber-50 border border-amber-200 p-3 mb-4 text-left" data-testid="promo-rejected-banner">
                  <p className="text-sm font-bold text-amber-800">Codice promo non applicato</p>
                  <p className="text-xs text-amber-700 mt-1">{promoResult.error}</p>
                  <p className="text-xs text-amber-700 mt-1">Nessun problema: hai comunque 7 giorni di prova gratuita.</p>
                </div>
              )}
              <p className="text-sm text-zinc-500 leading-relaxed mb-8">
                Clicca il link nell'email per attivare il tuo account e iniziare la prova gratuita di 7 giorni.
              </p>
              <div className="bg-zinc-50 border border-zinc-200 p-4 text-left">
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Non trovi l'email? Controlla la cartella spam. Se hai bisogno di aiuto scrivi a{' '}
                  <a href="mailto:vitadahost@gmail.com" className="text-teal-700 font-medium">vitadahost@gmail.com</a>
                </p>
              </div>
              <p className="mt-6 text-sm text-zinc-500">
                <Link to="/login" className="text-zinc-900 font-bold hover:underline">
                  Torna al login
                </Link>
              </p>
            </div>
          ) : (
            /* Step 1: Registration form */
            <>
              <p className="text-zinc-500 mb-2">Crea un account per iniziare</p>
              <p className="text-sm text-green-600 mb-8 font-medium">7 giorni di prova gratuita!</p>

              <form onSubmit={handleSubmit} className="space-y-6 text-left">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
                    <p>{error}</p>
                    {error.toLowerCase().includes('già registrata') && (
                      <p className="mt-2 text-xs">
                        Se ti sei registrata ma non hai ricevuto il link, <Link to="/verify-email" className="underline font-bold">reinvia il link di verifica</Link>.
                        Se hai già un account, <Link to="/login" className="underline font-bold">accedi qui</Link>.
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-bold uppercase tracking-wider text-zinc-700 block">
                    Nome
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="rounded-none border-zinc-300 focus:ring-zinc-900 focus:border-zinc-900"
                    placeholder="Il tuo nome"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wider text-zinc-700 block">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rounded-none border-zinc-300 focus:ring-zinc-900 focus:border-zinc-900"
                    placeholder="nome@esempio.com"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wider text-zinc-700 block">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="rounded-none border-zinc-300 focus:ring-zinc-900 focus:border-zinc-900"
                    placeholder="Minimo 6 caratteri"
                    data-testid="register-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="promo_code" className="text-sm font-bold uppercase tracking-wider text-zinc-700 block">
                    Codice promo <span className="text-zinc-400 font-normal normal-case tracking-normal">(opzionale)</span>
                  </Label>
                  <Input
                    id="promo_code"
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    className="rounded-none border-zinc-300 focus:ring-zinc-900 focus:border-zinc-900 font-mono tracking-wider uppercase"
                    placeholder="Es. VDH2026"
                    autoComplete="off"
                    maxLength={20}
                    data-testid="register-promo-input"
                  />
                  <p className="text-xs text-zinc-400">Se hai un codice promo (concorso, evento, partner), inseriscilo qui per attivare subito il tuo piano.</p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer" data-testid="gdpr-consent-label">
                  <input
                    type="checkbox"
                    checked={gdprAccepted}
                    onChange={(e) => setGdprAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 border-zinc-300 accent-zinc-900 flex-shrink-0"
                    data-testid="gdpr-consent-checkbox"
                  />
                  <span className="text-xs text-zinc-500 leading-relaxed">
                    Accetto i <Link to="/terms" className="underline text-zinc-700">Termini e Condizioni</Link> e l'<Link to="/privacy" className="underline text-zinc-700">Informativa Privacy</Link>. Confermo di aver letto le modalita di trattamento dei dati ai sensi del GDPR.
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={loading || !gdprAccepted}
                  className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider py-6 disabled:opacity-40"
                  data-testid="register-submit-button"
                >
                  {loading ? 'Invio in corso...' : 'Registrati'}
                </Button>
              </form>

              <p className="mt-8 text-zinc-500">
                Hai gia un account?{' '}
                <Link to="/login" className="text-zinc-900 font-bold hover:underline" data-testid="login-link">
                  Accedi
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
